import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { Page } from '../../../../../domain/page';
import { fetchPages } from '../../page-selector.actions';
import { AppState } from '../../page-selector.reducer';
import { selectIsLoading, selectPageById, selectPagesFiltered } from '../../page-selector.selectors';
import { PageSelectorViewModel } from './page-selector.view-model';

@Injectable()
export class PageSelectorService {
  private readonly filter$ = new BehaviorSubject<string>('');
  public readonly isLoading$ = this.store.select(selectIsLoading);

  constructor(private readonly store: Store<AppState>) {}

  public vm$: Observable<PageSelectorViewModel[]> = this.filter$.pipe(
    distinctUntilChanged(),
    switchMap((term) => {
      return this.store.pipe(
        select(selectPagesFiltered(term)),
        map((pages) => pages.map((page) => this.mapPageToViewModel(page)))
      );
    })
  );

  public fetchPages(excludeDrafts?: boolean) {
    this.store.dispatch(fetchPages({ term: undefined, excludeDrafts }));
  }

  public search(term?: string) {
    this.filter$.next(term ?? '');
  }
  public prefillData$(id: string): Observable<PageSelectorViewModel> {
    return this.store.pipe(
      select(selectPageById(id)),
      filter((v) => !!v),
      take(1),
      map((page) => this.mapPageToViewModel(page))
    );
  }

  private mapPageToViewModel(page?: Page): PageSelectorViewModel {
    return page
      ? {
          id: page.id,
          pageType: page.type.stringValue,
          name:
            page.pageVersions.find((pageVersion) => pageVersion.status === 'PUBLISHED')?.titleEnglish ||
            page.pageVersions.find((pageVersion) => pageVersion.status === 'DRAFT')?.titleEnglish ||
            '',
          internalName:
            page.pageVersions.find((pageVersion) => pageVersion.status === 'PUBLISHED')?.internalName ||
            page.pageVersions.find((pageVersion) => pageVersion.status === 'DRAFT')?.internalName ||
            '',
          lastPublishedAt: page.lastPublishedAt,
          status: page.pageVersions
            .map((pageVersion) => {
              return pageVersion.status === 'PUBLISHED' ? 'Live' : pageVersion.status === 'DRAFT' ? 'Draft' : '';
            })
            .filter((part) => !!part)
            .reduce((parts, part) => Array.from(new Set([...parts, part])), [])
            .join(' & '),
        }
      : undefined;
  }
}
